import React, { memo, useCallback, useMemo, useState } from "react";

import {
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import colors from "app/colors";
import {
  AdminEditUserMutation,
  AdminEditUserMutationVariables,
  UserAttributeFragment,
} from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import { useUsers } from "modules/providers/users-provider";
import AdminUserAttributesSelector from "./admin-user/admin-user-attributes-selector";
import ADMIN_EDIT_USER_MUTATION from "./_networking/admin-edit-user";
import { useUserAttributes } from "modules/providers/user-attributes-provider";

interface IProps {
  userId: string;
}

const AdminEditUserAttributesModal = ({ userId }: IProps) => {
  const { closeModal } = useOverlay();
  const { usersById, userAttributesByUserId } = useUsers();
  const { userAttributeCategories, userAttributesById } = useUserAttributes();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const user = useMemo(() => usersById[userId], [usersById, userId]);

  const _userAttributes = useMemo(
    () => userAttributesByUserId[userId] || [],
    [userAttributesByUserId, userId],
  );

  const [userAttributes, setUserAttributes] = useState<UserAttributeFragment[]>(
    user?.userAttributeIdList.map(
      (userAttributeId) => userAttributesById[userAttributeId],
    ) as UserAttributeFragment[],
  );

  const userAttributeIdList = useMemo(
    () => userAttributes.map((userAttribute) => userAttribute.id),
    [userAttributes],
  );

  const [adminEditUserMutationAPI] = useMutation<
    AdminEditUserMutation,
    AdminEditUserMutationVariables
  >(ADMIN_EDIT_USER_MUTATION);

  const onSaveClicked = useCallback(async () => {
    if (!user) return;
    setIsSubmitting(true);
    adminEditUserMutationAPI({
      variables: {
        id: user.id,
        input: {
          userAttributeIdList,
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        adminEditUser: {
          __typename: "User",
          ...user,
          userAttributeIdList,
        },
      },
      onError: () => {
        //TODO: handle error
        setIsSubmitting(false);
      },
      onCompleted: () => {
        setIsSubmitting(false);
        closeModal();
      },
    });
  }, [adminEditUserMutationAPI, closeModal, user, userAttributeIdList]);

  if (!user) return null;

  return (
    <Modal isOpen onClose={closeModal} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <Stack w="full" spacing={4} py={6} alignItems="center">
            <VStack w="full">
              <Text
                fontFamily="Outfit"
                fontWeight={700}
                fontSize={24}
                color={colors.blackSecondary}
              >
                Edit User Attributes
              </Text>
            </VStack>

            <VStack
              w="full"
              spacing={4}
              pt={4}
              px={4}
              divider={<Divider />}
              maxH={500}
              overflowY="auto"
              pb={300}
            >
              {userAttributeCategories.map((userAttributeCategory) => {
                const _setUserAttributes = (
                  newUserAttributes: UserAttributeFragment[],
                ) => {
                  //need to remove the attributes in the category that are not in the new attrubtes
                  setUserAttributes((prevUserAttributes) => {
                    const updatedUserAttributes = [
                      ...prevUserAttributes,
                    ].filter(
                      (userAttribute) =>
                        userAttribute.userAttributeCategoryId !==
                        userAttributeCategory.id,
                    );
                    updatedUserAttributes.push(...newUserAttributes);
                    return updatedUserAttributes;
                  });
                };

                return (
                  <HStack spacing={2} w="full" key={userAttributeCategory.id}>
                    <Text
                      minW={200}
                      color={colors.blackSecondary}
                      fontWeight={700}
                      fontSize={14}
                      noOfLines={1}
                      textTransform="capitalize" //https://teamcake.atlassian.net/browse/SYN-2262
                    >
                      {userAttributeCategory.name}
                    </Text>
                    <AdminUserAttributesSelector
                      userAttributeCategory={userAttributeCategory}
                      onSelectedOptionsChanged={_setUserAttributes}
                      defaultValue={
                        _userAttributes.find(
                          (obj) =>
                            obj.userAttributeCategory.id ===
                            userAttributeCategory.id,
                        )?.userAttributes
                      }
                    />
                  </HStack>
                );
              })}
            </VStack>
            <HStack w="full" spacing={4} justifyContent="center">
              <Button
                fontWeight={700}
                fontSize={16}
                variant="cancel"
                height={50}
                w={140}
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                fontWeight={700}
                fontSize={16}
                height={50}
                w={140}
                onClick={onSaveClicked}
              >
                {isSubmitting ? <Spinner size="sm" /> : "Save Attributes"}
              </Button>
            </HStack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminEditUserAttributesModal);
